/**
 * Shared utilities for block renderers
 */

import DOMPurify from 'dompurify';

// =============================================================================
// STATE RENDERING UTILITIES
// =============================================================================

/**
 * SVG icons for state displays
 */
const STATE_ICONS = {
  error: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>`,
  empty: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
  </svg>`,
  noItems: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    <line x1="9" y1="14" x2="15" y2="14"/>
  </svg>`,
  incomplete: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>`,
};

/**
 * Create a styled error state element.
 * Matches the dark theme aesthetic with subtle error tinting.
 *
 * @param message - The error message to display
 * @param options - Optional configuration
 * @returns HTML string for the error state
 *
 * @example
 * container.innerHTML = createErrorState('Unable to load content');
 * container.innerHTML = createErrorState('Invalid data format', { compact: true });
 */
export function createErrorState(
  message: string,
  options: {
    compact?: boolean;
    title?: string;
  } = {}
): string {
  const { compact = false, title } = options;
  const escapedMessage = escapeHtml(message);
  const escapedTitle = title ? escapeHtml(title) : null;

  if (compact) {
    return `
      <div class="pms-block-error pms-block-error-compact">
        <div class="pms-block-error-icon">${STATE_ICONS.error}</div>
        <span class="pms-block-error-text">${escapedMessage}</span>
      </div>
    `;
  }

  return `
    <div class="pms-block-error">
      <div class="pms-block-error-icon">${STATE_ICONS.error}</div>
      <div class="pms-block-error-content">
        ${escapedTitle ? `<div class="pms-block-error-title">${escapedTitle}</div>` : ''}
        <div class="pms-block-error-text">${escapedMessage}</div>
      </div>
    </div>
  `;
}

/**
 * Create a styled empty state element.
 * Provides helpful feedback when no items are available.
 *
 * @param message - Optional custom message (default: "No items to display")
 * @param options - Optional configuration
 * @returns HTML string for the empty state
 *
 * @example
 * container.innerHTML = createEmptyState();
 * container.innerHTML = createEmptyState('No results found', { compact: true });
 * container.innerHTML = createEmptyState('No items', { title: 'Empty List' });
 */
export function createEmptyState(
  message?: string,
  options: {
    compact?: boolean;
    title?: string;
    icon?: 'empty' | 'noItems';
  } = {}
): string {
  const { compact = false, title, icon = 'noItems' } = options;
  const displayMessage = message || 'No items to display';
  const escapedMessage = escapeHtml(displayMessage);
  const escapedTitle = title ? escapeHtml(title) : null;
  const iconSvg = STATE_ICONS[icon] || STATE_ICONS.noItems;

  if (compact) {
    return `
      <div class="pms-block-empty pms-block-empty-compact">
        <div class="pms-block-empty-icon">${iconSvg}</div>
        <span class="pms-block-empty-text">${escapedMessage}</span>
      </div>
    `;
  }

  return `
    <div class="pms-block-empty">
      <div class="pms-block-empty-icon">${iconSvg}</div>
      ${escapedTitle ? `<div class="pms-block-empty-title">${escapedTitle}</div>` : ''}
      <div class="pms-block-empty-text">${escapedMessage}</div>
    </div>
  `;
}

/**
 * Create a styled incomplete state element.
 * Used when stream ends mid-block.
 *
 * @param message - Optional custom message
 * @returns HTML string for the incomplete state
 */
export function createIncompleteState(message?: string): string {
  const displayMessage = message || 'Content was truncated';
  const escapedMessage = escapeHtml(displayMessage);

  return `
    <div class="pms-block-incomplete">
      <div class="pms-block-incomplete-icon">${STATE_ICONS.incomplete}</div>
      <span>${escapedMessage}</span>
    </div>
  `;
}

/**
 * Reusable element for HTML escaping.
 * Using a single element avoids memory pressure in high-frequency contexts.
 */
let htmlEncoder: HTMLDivElement | null = null;

/**
 * Escape HTML to prevent XSS attacks.
 * Uses DOM API for reliable escaping.
 * Reuses a single element to avoid memory pressure in streaming contexts.
 */
export function escapeHtml(text: string): string {
  if (!htmlEncoder) {
    htmlEncoder = document.createElement('div');
  }
  htmlEncoder.textContent = text;
  return htmlEncoder.innerHTML;
}

/**
 * Sanitize HTML using DOMPurify.
 * Use this for any HTML that will be inserted via innerHTML.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'code', 'a', 'br', 'span', 'div', 'p', 'ul', 'li', 'img', 'h1', 'h2', 'h3', 'h4', 'hr'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'loading'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Validate URL to prevent javascript: and other dangerous protocols.
 */
export function isUrlSafe(url: string): boolean {
  try {
    const trimmed = url.trim().toLowerCase();
    if (trimmed.startsWith('javascript:') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('vbscript:') ||
        trimmed.startsWith('file:')) {
      return false;
    }
    if (trimmed.startsWith('/') ||
        trimmed.startsWith('#') ||
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('mailto:')) {
      return true;
    }
    if (!trimmed.includes(':')) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Create a block wrapper element with standard classes.
 * Includes pms-block-animate class for entrance animation (fade-in + slide-up).
 */
export function createBlockWrapper(blockType: string, additionalClasses?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = `pms-block pms-block-${blockType} pms-block-animate${additionalClasses ? ' ' + additionalClasses : ''}`;
  return wrapper;
}

/**
 * Format a number with locale-specific formatting.
 */
export function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Parse a hex color string to RGB values.
 * Supports #RGB, #RRGGBB formats.
 */
export function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '');

  if (cleaned.length === 3) {
    // #RGB -> #RRGGBB
    const r = parseInt(cleaned[0]! + cleaned[0]!, 16);
    const g = parseInt(cleaned[1]! + cleaned[1]!, 16);
    const b = parseInt(cleaned[2]! + cleaned[2]!, 16);
    return { r, g, b };
  } else if (cleaned.length === 6) {
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return { r, g, b };
  }

  return null;
}

/**
 * Calculate relative luminance of a color (WCAG formula).
 * Returns a value between 0 (black) and 1 (white).
 */
export function calculateLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number): number => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Determine if a color is "dark" (needs light text) or "light" (needs dark text).
 * Returns true if the color is dark, false if light.
 * Uses WCAG contrast ratio guidelines (threshold ~0.179 for 4.5:1 contrast with white).
 */
export function isColorDark(color: string): boolean {
  // Handle hex colors
  if (color.startsWith('#')) {
    const rgb = parseHexColor(color);
    if (rgb) {
      const luminance = calculateLuminance(rgb.r, rgb.g, rgb.b);
      return luminance < 0.179; // Dark color needs light text
    }
  }

  // Handle rgb/rgba
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]!, 10);
    const g = parseInt(rgbMatch[2]!, 10);
    const b = parseInt(rgbMatch[3]!, 10);
    const luminance = calculateLuminance(r, g, b);
    return luminance < 0.179;
  }

  // Default to assuming dark (our default theme is dark)
  return true;
}

/**
 * Safely evaluate a simple math formula.
 * Only allows numbers and basic math operators (+, -, *, /, parentheses).
 * Rejects any other characters to prevent code injection.
 */
export function evaluateFormula(formula: string, value: number): number | null {
  try {
    // Replace 'value' placeholder with actual value
    const expression = formula.replace(/value/g, String(value));

    // Security: Only allow numbers, operators, parentheses, and whitespace
    // This regex rejects any letters, function calls, or dangerous characters
    const safePattern = /^[\d\s+\-*/().]+$/;
    if (!safePattern.test(expression)) {
      console.warn('[Security] Formula rejected - contains unsafe characters:', formula);
      return null;
    }

    // Additional check: reject if it looks like a function call
    if (/[a-zA-Z]/.test(expression)) {
      console.warn('[Security] Formula rejected - contains letters:', formula);
      return null;
    }

    // Use Function constructor for controlled eval (now safe due to validation)
    const result = Function(`"use strict"; return (${expression})`)();

    // Ensure result is a finite number
    if (typeof result !== 'number' || !isFinite(result)) {
      return null;
    }

    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch {
    return null;
  }
}

// =============================================================================
// VIDEO & EMBED HELPERS
// =============================================================================

/**
 * Extract video ID and platform from a YouTube or Vimeo URL.
 * Returns null if the URL is not a valid video URL.
 */
export function extractVideoId(url: string): { platform: 'youtube' | 'vimeo'; id: string } | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const trimmed = url.trim();

    // YouTube patterns:
    // - https://www.youtube.com/watch?v=VIDEO_ID
    // - https://youtu.be/VIDEO_ID
    // - https://www.youtube.com/embed/VIDEO_ID
    // - https://youtube.com/shorts/VIDEO_ID
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/.*[?&]v=([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of youtubePatterns) {
      const match = trimmed.match(pattern);
      if (match && match[1]) {
        return { platform: 'youtube', id: match[1] };
      }
    }

    // Vimeo patterns:
    // - https://vimeo.com/VIDEO_ID
    // - https://player.vimeo.com/video/VIDEO_ID
    const vimeoPatterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
    ];

    for (const pattern of vimeoPatterns) {
      const match = trimmed.match(pattern);
      if (match && match[1]) {
        return { platform: 'vimeo', id: match[1] };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Build a secure embed URL for a video platform.
 * Uses privacy-enhanced domains where available.
 */
export function buildVideoEmbedUrl(
  platform: 'youtube' | 'vimeo',
  videoId: string,
  privacyEnhanced: boolean = true
): string {
  if (platform === 'youtube') {
    // Use youtube-nocookie.com for privacy-enhanced mode
    const domain = privacyEnhanced ? 'www.youtube-nocookie.com' : 'www.youtube.com';
    return `https://${domain}/embed/${videoId}?rel=0&modestbranding=1`;
  } else if (platform === 'vimeo') {
    return `https://player.vimeo.com/video/${videoId}?dnt=1`;
  }
  return '';
}

/**
 * Allowed domains for EMBED block.
 * Only these domains can be embedded for security.
 */
const ALLOWED_EMBED_DOMAINS = [
  'figma.com',
  'www.figma.com',
  'codepen.io',
  'codesandbox.io',
  'calendly.com',
  'typeform.com',
  'loom.com',
  'www.loom.com',
  'miro.com',
  'app.miro.com',
  'airtable.com',
  'notion.so',
  'docs.google.com',
  'forms.google.com',
  'slides.google.com',
  'canva.com',
  'pitch.com',
  'prezi.com',
  'spotify.com',
  'open.spotify.com',
  'soundcloud.com',
];

/**
 * Check if a URL is from an allowed embed domain.
 * Returns false for disallowed or invalid URLs.
 */
export function isAllowedEmbedDomain(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(url.trim());
    const hostname = parsed.hostname.toLowerCase();

    // Check exact match or subdomain match
    return ALLOWED_EMBED_DOMAINS.some(domain => {
      return hostname === domain || hostname.endsWith('.' + domain);
    });
  } catch {
    return false;
  }
}

/**
 * Get sandbox attributes for an iframe based on the domain.
 * More permissive for known safe domains, restrictive for others.
 */
export function getIframeSandbox(url: string): string {
  // Base sandbox - very restrictive
  const baseSandbox = 'allow-scripts allow-same-origin';

  // Some embeds need forms (Typeform, Calendly)
  if (url.includes('typeform.com') || url.includes('calendly.com') || url.includes('forms.google.com')) {
    return baseSandbox + ' allow-forms allow-popups';
  }

  // Interactive tools (Figma, Miro, etc.)
  if (url.includes('figma.com') || url.includes('miro.com') || url.includes('canva.com')) {
    return baseSandbox + ' allow-popups';
  }

  return baseSandbox;
}

/**
 * Calculate aspect ratio padding percentage.
 */
export function getAspectRatioPadding(aspectRatio?: '16:9' | '4:3' | '1:1' | '21:9'): string {
  switch (aspectRatio) {
    case '4:3':
      return '75%';
    case '1:1':
      return '100%';
    case '21:9':
      return '42.86%';
    case '16:9':
    default:
      return '56.25%';
  }
}

/**
 * Add error handling to an image element.
 * Adds 'pms-img-error' class on load failure for CSS-based fallback.
 * If hideParentCard is true and image is inside a card, hides the entire card.
 */
export function handleImageError(img: HTMLImageElement, hideParentCard: boolean = true): void {
  const handleError = () => {
    img.classList.add('pms-img-error');
    // Hide the broken image icon by making it invisible
    img.style.visibility = 'hidden';

    // If image is inside a card, hide the entire card
    if (hideParentCard) {
      const card = img.closest('.pms-card');
      if (card) {
        card.classList.add('pms-card-image-error');
      }
    }
  };

  // Check if image already failed (complete but no natural dimensions)
  if (img.complete && img.naturalWidth === 0) {
    handleError();
    return;
  }

  // Listen for future errors
  img.addEventListener('error', handleError, { once: true });
}

/**
 * Add error handling to all images within a container.
 * Call after setting innerHTML to attach handlers.
 */
export function handleImagesInContainer(container: HTMLElement): void {
  const images = container.querySelectorAll('img');
  images.forEach(img => handleImageError(img as HTMLImageElement));
}

// =============================================================================
// CLICK PROTECTION
// =============================================================================

/**
 * Disable a button temporarily to prevent spam clicking.
 * Re-enables after timeout or when manually re-enabled.
 *
 * @param button The button element to disable
 * @param timeoutMs How long to disable (default: 2000ms)
 * @returns Function to manually re-enable the button
 */
export function disableButtonTemporarily(
  button: HTMLButtonElement,
  timeoutMs = 2000
): () => void {
  button.disabled = true;
  button.classList.add('pms-processing');

  const timeoutId = setTimeout(() => {
    button.disabled = false;
    button.classList.remove('pms-processing');
  }, timeoutMs);

  // Return function to manually re-enable (cancels timeout)
  return () => {
    clearTimeout(timeoutId);
    button.disabled = false;
    button.classList.remove('pms-processing');
  };
}

/**
 * Wrap a click handler with spam protection.
 * Prevents multiple clicks within the cooldown period.
 *
 * @param handler The click handler function
 * @param cooldownMs Minimum time between clicks (default: 1000ms)
 */
export function withClickProtection<T extends HTMLElement>(
  handler: (e: Event, element: T) => void,
  cooldownMs = 1000
): (e: Event) => void {
  let lastClick = 0;

  return (e: Event) => {
    const now = Date.now();
    if (now - lastClick < cooldownMs) {
      e.preventDefault();
      return;
    }
    lastClick = now;

    const element = e.currentTarget as T;

    // If it's a button, disable it temporarily
    if (element instanceof HTMLButtonElement) {
      disableButtonTemporarily(element, cooldownMs);
    }

    handler(e, element);
  };
}

// =============================================================================
// SKELETON PLACEHOLDERS
// =============================================================================

/**
 * Block type to skeleton configuration mapping.
 * Defines the appropriate skeleton structure for each block type.
 */
interface SkeletonConfig {
  /** Number of skeleton items to show (for grid layouts) */
  count?: number;
  /** Layout type */
  layout: 'cards' | 'stats' | 'table' | 'list' | 'process' | 'text' | 'image' | 'generic';
  /** Number of text lines for text-based skeletons */
  lines?: number;
  /** Number of table rows */
  rows?: number;
  /** Number of table columns */
  cols?: number;
}

const SKELETON_CONFIGS: Record<string, SkeletonConfig> = {
  // Card-based blocks
  cards: { layout: 'cards', count: 3 },
  browse: { layout: 'cards', count: 4 },
  pricing: { layout: 'cards', count: 3 },
  team: { layout: 'cards', count: 3 },
  gallery: { layout: 'cards', count: 4 },

  // Stats/metrics blocks
  stats: { layout: 'stats', count: 4 },

  // Table blocks
  table: { layout: 'table', rows: 4, cols: 3 },

  // List blocks
  list: { layout: 'list', count: 4 },
  faq: { layout: 'list', count: 3 },
  timeline: { layout: 'list', count: 3 },

  // Process/steps blocks
  process: { layout: 'process', count: 4 },

  // Text-heavy blocks
  text: { layout: 'text', lines: 3 },
  hero: { layout: 'text', lines: 2 },
  feature: { layout: 'text', lines: 4 },
  comparison: { layout: 'text', lines: 4 },
  testimonial: { layout: 'text', lines: 3 },
  alert: { layout: 'text', lines: 2 },

  // Image/media blocks
  video: { layout: 'image' },
  embed: { layout: 'image' },
  map: { layout: 'image' },
  logos: { layout: 'cards', count: 5 },
  code: { layout: 'text', lines: 6 },

  // Interactive blocks
  interactive: { layout: 'generic' },
  flowform: { layout: 'generic' },
  livesection: { layout: 'cards', count: 3 },
  tabs: { layout: 'generic' },

  // Calculators
  mortgagecalc: { layout: 'generic' },
  calculator: { layout: 'generic' },
};

/**
 * Create a skeleton placeholder element for a given block type.
 * Returns appropriate skeleton HTML based on the block type hint.
 *
 * @param type Block type identifier (e.g., 'cards', 'stats', 'table')
 * @returns HTML string for the skeleton placeholder
 */
export function createSkeletonPlaceholder(type: string): string {
  const normalizedType = type.toLowerCase().replace(/[_-]/g, '');
  const config = SKELETON_CONFIGS[normalizedType] || { layout: 'generic' };

  switch (config.layout) {
    case 'cards':
      return createCardsSkeleton(config.count || 3);

    case 'stats':
      return createStatsSkeleton(config.count || 4);

    case 'table':
      return createTableSkeleton(config.rows || 4, config.cols || 3);

    case 'list':
      return createListSkeleton(config.count || 4);

    case 'process':
      return createProcessSkeleton(config.count || 4);

    case 'text':
      return createTextSkeleton(config.lines || 3);

    case 'image':
      return createImageSkeleton();

    case 'generic':
    default:
      return createGenericSkeleton();
  }
}

/**
 * Create skeleton for card-based blocks (cards, browse, pricing, etc.)
 */
function createCardsSkeleton(count: number): string {
  const cards = Array(count)
    .fill(null)
    .map(
      () => `
      <div class="pms-skeleton-card">
        <div class="pms-skeleton-card-image"></div>
        <div class="pms-skeleton-card-body">
          <div class="pms-skeleton-text pms-skeleton-text--medium pms-skeleton-text--title"></div>
          <div class="pms-skeleton-text pms-skeleton-text--long"></div>
          <div class="pms-skeleton-text pms-skeleton-text--short"></div>
        </div>
      </div>
    `
    )
    .join('');

  return `
    <div class="pms-skeleton-container pms-skeleton-stagger">
      <div class="pms-skeleton-grid pms-skeleton-grid--auto">
        ${cards}
      </div>
    </div>
  `;
}

/**
 * Create skeleton for stats/metrics blocks
 */
function createStatsSkeleton(count: number): string {
  const stats = Array(count)
    .fill(null)
    .map(
      () => `
      <div class="pms-skeleton-stat">
        <div class="pms-skeleton-stat-value"></div>
        <div class="pms-skeleton-stat-label"></div>
      </div>
    `
    )
    .join('');

  return `
    <div class="pms-skeleton-container pms-skeleton-stagger">
      <div class="pms-skeleton-grid pms-skeleton-grid--${count > 3 ? '4' : count}">
        ${stats}
      </div>
    </div>
  `;
}

/**
 * Create skeleton for table blocks
 */
function createTableSkeleton(rows: number, cols: number): string {
  const headerCells = Array(cols)
    .fill(null)
    .map(() => '<div class="pms-skeleton-table-cell"></div>')
    .join('');

  const bodyRows = Array(rows)
    .fill(null)
    .map(
      () => `
      <div class="pms-skeleton-table-row">
        ${Array(cols)
          .fill(null)
          .map(() => '<div class="pms-skeleton-table-cell"></div>')
          .join('')}
      </div>
    `
    )
    .join('');

  return `
    <div class="pms-skeleton-container">
      <div class="pms-skeleton-table">
        <div class="pms-skeleton-table-header">
          ${headerCells}
        </div>
        ${bodyRows}
      </div>
    </div>
  `;
}

/**
 * Create skeleton for list blocks (list, faq, timeline)
 */
function createListSkeleton(count: number): string {
  const items = Array(count)
    .fill(null)
    .map(
      () => `
      <div class="pms-skeleton-list-item">
        <div class="pms-skeleton-list-icon"></div>
        <div class="pms-skeleton-list-content">
          <div class="pms-skeleton-text pms-skeleton-text--medium"></div>
          <div class="pms-skeleton-text pms-skeleton-text--long pms-skeleton-text--small"></div>
        </div>
      </div>
    `
    )
    .join('');

  return `
    <div class="pms-skeleton-container pms-skeleton-stagger">
      <div class="pms-skeleton-list">
        ${items}
      </div>
    </div>
  `;
}

/**
 * Create skeleton for process/steps blocks
 */
function createProcessSkeleton(count: number): string {
  const steps = Array(count)
    .fill(null)
    .map(
      () => `
      <div class="pms-skeleton-step">
        <div class="pms-skeleton-step-number"></div>
        <div class="pms-skeleton-step-content">
          <div class="pms-skeleton-text pms-skeleton-text--medium pms-skeleton-text--title"></div>
          <div class="pms-skeleton-text pms-skeleton-text--long pms-skeleton-text--small"></div>
        </div>
      </div>
    `
    )
    .join('');

  return `
    <div class="pms-skeleton-container pms-skeleton-stagger">
      <div class="pms-skeleton-process">
        ${steps}
      </div>
    </div>
  `;
}

/**
 * Create skeleton for text-heavy blocks
 */
function createTextSkeleton(lines: number): string {
  const widths = ['full', 'long', 'medium', 'long', 'short', 'medium'];
  const textLines = Array(lines)
    .fill(null)
    .map((_, i) => {
      const width = widths[i % widths.length];
      return `<div class="pms-skeleton-text pms-skeleton-text--${width}"></div>`;
    })
    .join('');

  return `
    <div class="pms-skeleton-container">
      <div class="pms-skeleton-text pms-skeleton-text--medium pms-skeleton-text--heading"></div>
      ${textLines}
    </div>
  `;
}

/**
 * Create skeleton for image/media blocks (video, embed, map)
 */
function createImageSkeleton(): string {
  return `
    <div class="pms-skeleton-container">
      <div class="pms-skeleton-image pms-skeleton-image--landscape"></div>
    </div>
  `;
}

/**
 * Create generic skeleton for unknown or complex blocks
 */
function createGenericSkeleton(): string {
  return `
    <div class="pms-skeleton-container">
      <div class="pms-skeleton-text pms-skeleton-text--short pms-skeleton-text--title"></div>
      <div class="pms-skeleton-text pms-skeleton-text--full"></div>
      <div class="pms-skeleton-text pms-skeleton-text--long"></div>
      <div style="display: flex; gap: 12px; margin-top: 16px;">
        <div class="pms-skeleton-button pms-skeleton-button--medium"></div>
        <div class="pms-skeleton-button pms-skeleton-button--medium"></div>
      </div>
    </div>
  `;
}

/**
 * Create a skeleton placeholder DOM element.
 * Use this when you need a DOM element instead of HTML string.
 *
 * @param type Block type identifier
 * @returns HTMLElement containing the skeleton placeholder
 */
export function createSkeletonElement(type: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-block-skeleton';
  wrapper.innerHTML = createSkeletonPlaceholder(type);
  return wrapper;
}
